/* Align the mobile wall after a small native page scroll through its introduction.
   Never consume gestures; rearm only after a full return to the introduction. */
(() => {
  if (window.__glintWallScroll) return;
  window.__glintWallScroll = true;
  const instances = new Map();

  function mount(root) {
    if (instances.has(root)) return;
    const stage = root.querySelector('.glint-ws__stage');
    const intro = root.querySelector('.glint-ws__head');
    if (!stage || !intro) return;
    const mobile = matchMedia('(max-width: 749px)');
    const reduced = matchMedia('(prefers-reduced-motion: reduce)');
    const excluded = 'button,a,input,select,textarea,[contenteditable],model-viewer,dialog,.glint-ws__slot,.glint-ws__floor-piece,.glint-ws__panel,.glint-ws__checkout';
    const listeners = [];
    let gesture = null, timer = 0, frame = 0, used = false, width = innerWidth;
    let lastY = scrollY, touch = null, pointerDown = false, rejectedTouch = false;
    let ignoreUntil = performance.now() + 350;

    function on(target, type, fn, options = { passive: true }) {
      target.addEventListener(type, fn, options);
      listeners.push(() => target.removeEventListener(type, fn, options));
    }
    function blocked() {
      return !root.isConnected || !mobile.matches || reduced.matches || document.hidden ||
        (window.visualViewport && window.visualViewport.scale > 1.01) ||
        document.querySelector('dialog[open],details[open]#Details-menu-drawer-container') ||
        document.activeElement?.matches('input,textarea,select,[contenteditable]') ||
        /hidden|clip/.test(getComputedStyle(document.documentElement).overflowY) ||
        /hidden|clip/.test(getComputedStyle(document.body).overflowY);
    }
    function headerBottom() {
      let bottom = window.visualViewport?.offsetTop || 0;
      document.querySelectorAll('#header-group > *, .header-section').forEach(el => {
        const style = getComputedStyle(el);
        const box = el.getBoundingClientRect();
        if ((style.position === 'sticky' || style.position === 'fixed') &&
            style.visibility !== 'hidden' && Number(style.opacity) > 0 &&
            box.height > 0 && box.bottom > 0 && box.top <= bottom + 2) {
          bottom = Math.max(bottom, box.bottom);
        }
      });
      return bottom;
    }
    function gap() { return stage.getBoundingClientRect().top - headerBottom(); }
    function atIntro() { return intro.getBoundingClientRect().top >= headerBottom() - 6; }
    function overshoot() { return Math.min(160, stage.getBoundingClientRect().height * 0.35); }
    function touching() { return !!touch || pointerDown; }
    function cancel() {
      clearTimeout(timer);
      cancelAnimationFrame(frame);
      timer = frame = 0;
      gesture = null;
    }
    function suppress() {
      cancel();
      ignoreUntil = performance.now() + 800;
    }
    function allowed(event) {
      return (root.contains(event.target) || event.target === document.body || event.target === document.documentElement) &&
        !event.composedPath().some(el => el.matches?.(excluded));
    }
    function input(event) {
      if (!allowed(event)) { suppress(); return false; }
      ignoreUntil = 0;
      if (atIntro()) used = false;
      return true;
    }
    function align() {
      timer = 0;
      if (!gesture || touching() || blocked() || performance.now() < ignoreUntil) { cancel(); return; }
      const moved = scrollY - gesture.start;
      const remaining = gap();
      gesture = null;
      // Catch modest native inertia on either side of the target, not a large flick.
      if (moved < 10 || remaining < -overshoot() || remaining > intro.getBoundingClientRect().height + 24) return;
      used = true;
      if (Math.abs(remaining) <= 1) return;
      const from = scrollY;
      const to = from + remaining;
      const start = performance.now();
      function tick(now) {
        if (blocked()) { cancel(); return; }
        const progress = Math.min(1, (now - start) / 260);
        const eased = 1 - Math.pow(1 - progress, 3);
        window.scrollTo({ top: from + (to - from) * eased, behavior: 'instant' });
        frame = progress < 1 ? requestAnimationFrame(tick) : 0;
      }
      frame = requestAnimationFrame(tick);
    }
    function settle() {
      clearTimeout(timer);
      if (gesture && !touching()) timer = setTimeout(align, 120);
    }
    function resize() {
      const changedWidth = innerWidth !== width;
      width = innerWidth;
      if (changedWidth || blocked()) suppress();
      else if (!frame) settle(); // Browser chrome can retract during the swipe.
    }
    on(document, 'touchstart', event => {
      cancel();
      rejectedTouch = event.touches.length !== 1 || !input(event);
      touch = event.touches[0] ? { x: event.touches[0].clientX, y: event.touches[0].clientY } : null;
      if (rejectedTouch) suppress();
    }, { passive: true, capture: true });
    on(document, 'touchmove', event => {
      if (!touch) return;
      if (event.touches.length !== 1) rejectedTouch = true;
      else {
        const dx = Math.abs(event.touches[0].clientX - touch.x);
        const dy = touch.y - event.touches[0].clientY;
        if (dy < -10 || dx > Math.max(24, Math.abs(dy) * 1.5)) rejectedTouch = true;
      }
      if (rejectedTouch) suppress();
    }, { passive: true, capture: true });
    function release(event) {
      if (event.touches.length) return;
      touch = null;
      pointerDown = false;
      if (rejectedTouch) suppress();
      else settle();
    }
    on(document, 'touchend', release, { passive: true, capture: true });
    // Some browsers cancel the touch stream when native scrolling takes over.
    on(document, 'touchcancel', release, { passive: true, capture: true });
    on(document, 'wheel', event => {
      if (frame) { cancel(); return; }
      if (event.ctrlKey || event.deltaY <= 0 || Math.abs(event.deltaX) > Math.abs(event.deltaY)) { suppress(); return; }
      input(event);
      settle();
    }, { passive: true, capture: true });
    on(window, 'scroll', () => {
      const previous = lastY;
      lastY = scrollY;
      if (frame) return;
      if (atIntro()) used = false;
      if (blocked() || performance.now() < ignoreUntil) { cancel(); return; }
      const delta = scrollY - previous;
      if (delta < -2) { cancel(); return; }
      if (delta <= 0 || used) return;
      const remaining = gap();
      if (remaining < -overshoot()) { used = true; cancel(); return; }
      // Actual page movement is the trigger, even when the browser supplies no touchstart.
      if (!gesture) {
        const previousGap = remaining + delta;
        if (previousGap < 8 || previousGap > intro.getBoundingClientRect().height + 48) return;
        gesture = { start: previous };
      }
      settle();
    });
    // Any fresh action or layout change takes precedence over the attraction.
    on(document, 'pointerdown', event => {
      cancel();
      pointerDown = event.pointerType === 'touch';
      input(event);
    }, { passive: true, capture: true });
    function releasePointer() { pointerDown = false; settle(); }
    on(document, 'pointerup', releasePointer, { passive: true, capture: true });
    on(document, 'pointercancel', releasePointer, { passive: true, capture: true });
    on(document, 'keydown', event => {
      cancel();
      if (event.key === 'ArrowDown' && !event.altKey && !event.ctrlKey && !event.metaKey && !event.shiftKey) {
        input(event);
      } else suppress();
    }, { passive: true, capture: true });
    // Capture before existing handlers call scrollIntoView for catalogue or review navigation.
    on(document, 'click', suppress, { passive: true, capture: true });
    on(document, 'focusin', suppress, { passive: true, capture: true });
    on(window, 'pageshow', () => { suppress(); lastY = scrollY; ignoreUntil = performance.now() + 350; });
    on(window, 'resize', resize);
    on(window, 'pagehide', cancel);
    on(document, 'visibilitychange', cancel);
    on(mobile, 'change', cancel);
    on(reduced, 'change', cancel);
    if (window.visualViewport) on(window.visualViewport, 'resize', resize);
    instances.set(root, () => { cancel(); listeners.forEach(remove => remove()); instances.delete(root); });
  }
  function init(scope) {
    if (scope.matches?.('[data-glint-wall-studio]')) mount(scope);
    scope.querySelectorAll('[data-glint-wall-studio]').forEach(mount);
  }
  init(document);
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => {
    instances.forEach((dispose, root) => { if (event.target === root || event.target.contains(root)) dispose(); });
  });
})();
